import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import _ from "lodash";
import { Button, Dropdown, Menu } from "antd";
import {
    CheckOutlined,
    EllipsisOutlined,
    EyeOutlined,
    DownOutlined,
    FileWordOutlined,
    FileZipOutlined,
    HistoryOutlined,
    // UploadOutlined,
} from "@ant-design/icons";
import { useHistory, useParams } from "react-router-dom";
import { Page } from "@app/components/page";
import { Viewer } from "@app/components/report-document";
import { ReportDocumentStatus } from "@app/constants";
import { events } from "@app/lib/store";
import { antdHelpers } from "@app/util";
import Status from "@app/components/report-document/status";

import reportDocumentsStore from "@app/state/store/report-document/report-documents";
import sectionsStore from "@app/state/store/report-document/report-document-sections";
import dictionaryStore from "@app/state/store/report/dictionary/pick";
import abbreviationStore from "@app/state/store/report/abbreviation/pick";
import citationsStore from "@app/state/store/report/citation/pick";
import commentStore from "@app/state/store/report-document/comment";
import documentStore from "@app/state/store/report-document/document";
import captisLinkStore from "@app/state/store/report-document/captis-link";
import sessionStore from "@app/state/store/session";

import ReportDocumentSidebar from "@app/components/report-document/sidebar";
import setup from "@app/state/store/projects/setup";

import "./report-document.scoped.scss";
import Loader from "@app/components/loader/content";
// import UploadButton from "@app/components/form/upload/button";
import docxImportStore from "@app/state/store/report-document/report-document-docx-import";
// import { UploadContextProvider } from "@app/components/form/upload/context";
import DocxImportDiffModal from "@app/components/report-document/import/docx-import-diff-modal";
import ReviewOptionsModal from "@app/components/review/review-options-modal";
import cursorPositionStore from "@app/state/store/editor/cursor-position";

const ReportDocument = observer(() => {
    const { project, reportDocumentId } = useParams();
    const history = useHistory();
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(false);
    const readonly = setup.isComplete;
    const [importModalVisible, setImportModalVisible] = useState(false);
    const [docxPreviewData, setDocxPreviewData] = useState({ sections: [], comments: [] });
    const [docName, setDocName] = useState("");
    const [acceptedSections, setAcceptedSections] = useState({});
    const [reviewModalVisible, setReviewModalVisible] = useState(false);

    const reportDocument = reportDocumentsStore.reportDocument;
    const canAccess = reportDocumentsStore.canAccess(reportDocument);

    useEffect(() => {
        sessionStorage.removeItem("manualSectionSwitch");

        load();

        events.on("reportDocument.viewer.reload", () => {
            load();
        });

        events.on("reportDocument.viewer.reload.metaData", async () => {
            await reportDocumentsStore.updateCurrentReportDocumentMetaData(reportDocumentId);
        });

        const handleCloseEdit = ({ sectionId }) => {
            setEditing((current) => (current?.section._id === sectionId ? null : current));
        };

        events.on("reportDocument.section.closeEdit", handleCloseEdit);

        // The user can manually refresh individual report nodes via the Refresh button.
        const handleSettingsSaved = (event) => {
            if (event.key === "reportSettingsSaved") {
                localStorage.removeItem("reportSettingsSaved");
            }
        };
        window.addEventListener("storage", handleSettingsSaved);

        return () => {
            documentStore.reset();
            commentStore.reset();
            abbreviationStore.resetUsage();
            captisLinkStore.reset();

            // Clear cursor positions and current document state when leaving the document
            cursorPositionStore.clearCurrentDocument();
            cursorPositionStore.clearProjectCursorPositions(project);

            events.removeAllListeners(["reportDocument.viewer.reload"]);
            events.removeAllListeners(["reportDocument.viewer.reload.metaData"]);
            events.removeListener("reportDocument.section.closeEdit", handleCloseEdit);
            window.removeEventListener("storage", handleSettingsSaved);
        };
    }, [project, reportDocumentId]);

    // Restore document/section state after load
    useEffect(() => {
        if (!reportDocument || !sectionsStore.sections.length || loading || readonly) {
            return;
        }

        const savedDocument = cursorPositionStore.loadCurrentDocument();

        if (!savedDocument) {
            return;
        }

        // Check if saved document matches current document
        if (savedDocument.documentId === reportDocumentId) {
            // Find the section that was being edited
            const savedSection = sectionsStore.sections.find(
                (section) => section._id === savedDocument.sectionId,
            );

            if (savedSection && !editing) {
                if (cursorPositionStore.isForceReleased(savedSection._id)) {
                    cursorPositionStore.clearCurrentDocument();
                    cursorPositionStore.clearForceReleased(savedSection._id);
                    return;
                }

                const sectionLockedBy = savedSection.lockedBy;
                if (
                    sectionLockedBy &&
                    sectionLockedBy._id &&
                    sectionLockedBy._id !== sessionStore.user._id
                ) {
                    cursorPositionStore.clearCurrentDocument();
                    return;
                }

                const verifyAndRestore = async () => {
                    try {
                        if (cursorPositionStore.isForceReleased(savedSection._id)) {
                            cursorPositionStore.clearCurrentDocument();
                            cursorPositionStore.clearForceReleased(savedSection._id);
                            return;
                        }

                        const lockedBy = await sectionsStore.isLocked(savedSection._id);

                        if (_.isEmpty(lockedBy) || lockedBy?._id === sessionStore.user._id) {
                            // Double-check section.lockedBy hasn't changed
                            const currentSection = sectionsStore.sections.find(
                                (section) => section._id === savedSection._id,
                            );
                            if (
                                !currentSection?.lockedBy ||
                                currentSection.lockedBy._id === sessionStore.user._id
                            ) {
                                setEditing({ section: savedSection });
                            } else {
                                cursorPositionStore.clearCurrentDocument();
                            }
                        } else {
                            // Lock belongs to another user, clear saved state
                            cursorPositionStore.clearCurrentDocument();
                        }
                    } catch (error) {
                        // On error, don't restore to be safe
                        cursorPositionStore.clearCurrentDocument();
                    }
                };

                const restoreTimer = setTimeout(() => {
                    verifyAndRestore();
                }, 300);

                return () => {
                    clearTimeout(restoreTimer);
                };
            }
        }
    }, [reportDocument, reportDocumentId, sectionsStore.sections, loading, readonly, editing]);

    // Open section edit from lock-transfer navigation (history.state set by lock.js)
    useEffect(() => {
        if (loading || !sectionsStore.sections.length) {
            return;
        }

        const openEditId = history.location.state?.openEditSectionId;
        if (openEditId) {
            history.replace({ ...history.location, state: {} });
            events.emit("reportDocument.section.openEdit", { sectionId: openEditId });
        }
    }, [loading, sectionsStore.sections.length]);

    if (!reportDocument || !canAccess) {
        return null;
    }

    async function load(sections) {
        setLoading(true);

        if (project) {
            reportDocumentsStore.setProjectId(project);
            await setup.load(project);
        }

        if (reportDocumentId) {
            sectionsStore.section = null;
            sectionsStore.sections = [];

            await reportDocumentsStore.load(reportDocumentId);

            if (reportDocumentsStore.reportDocument) {
                documentStore.init(
                    reportDocumentId,
                    reportDocumentsStore.reportDocumentSections,
                    true,
                );
                sectionsStore.sections = sections || reportDocumentsStore.reportDocumentSections;

                await dictionaryStore.load();
                await abbreviationStore.load();
                await commentStore.load(reportDocumentId);
                await citationsStore.load();
                await captisLinkStore.load();
            }
        }

        setLoading(false);
    }

    const close = () => {
        // Clear cursor positions and current document state when explicitly closing
        cursorPositionStore.clearCurrentDocument();
        cursorPositionStore.clearProjectCursorPositions(project);

        history.push(`../documents`);
    };

    const userReviewer = reportDocumentsStore.getUserReviewer(
        reportDocument.review?.stage?.reviewers,
    );
    const reviewStage = reportDocument.review?.stage;
    const reviewStarted = reviewStage?.title;
    const hasReviewers = reportDocument.review?.stage?.reviewers?.length > 0;

    const markReviewed = async () => {
        const hasPendingSections = reportDocumentsStore.hasSectionsPendingReview(reportDocumentId);
        if (hasPendingSections) {
            setReviewModalVisible(true);
        } else {
            await reportDocumentsStore.review(reportDocumentId);
        }
    };

    const handleReviewConfirm = async (option) => {
        setReviewModalVisible(false);

        if (option === "reviewed-only") {
            await reportDocumentsStore.review(reportDocumentId, { includeOnlyReviewed: true });
        } else {
            await reportDocumentsStore.review(reportDocumentId, { includeOnlyReviewed: false });
        }
    };

    const handleReviewCancel = () => {
        setReviewModalVisible(false);
    };

    const onMenu = async (action) => {
        switch (action.key) {
            case "versionHistory":
                history.push(`../documents/${reportDocumentId}/history`);
                break;
            case "startReview":
                await markReviewed();
                break;
            default:
                return;
        }
    };

    const menuItems = (
        <Menu onClick={onMenu} className="menu">
            {[ReportDocumentStatus.NOT_STARTED, ReportDocumentStatus.IN_PROGRESS].includes(
                reportDocument.status,
            ) &&
                !reviewStarted &&
                !readonly &&
                reportDocumentsStore.canUserStartReview && (
                    <Menu.Item key="startReview" icon={<EyeOutlined />}>
                        Send document for review
                    </Menu.Item>
                )}
            <Menu.Item key="versionHistory" icon={<HistoryOutlined />}>
                Version history
            </Menu.Item>
        </Menu>
    );

    const baseURL = `/api/project/${project}/report-documents/${reportDocumentId}`;
    const exportMenu = (
        <Menu>
            <Menu.Item key="word" icon={<FileWordOutlined />}>
                <a href={`${baseURL}/export?format=word`} target="_blank" rel="noopener noreferrer">
                    Word
                </a>
            </Menu.Item>
            <Menu.Item key="word" icon={<FileWordOutlined />}>
                <a
                    href={`${baseURL}/export?format=wordplus`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Word + Comments
                </a>
            </Menu.Item>
            <Menu.Item key="refs" icon={<FileZipOutlined />}>
                <a href={`${baseURL}/refPack`} target="_blank" rel="noopener noreferrer">
                    Reference Pack
                </a>
            </Menu.Item>
        </Menu>
    );

    // const handleDocxImport = async (fileObj) => {
    //     try {
    //         await docxImportStore.previewImportToExisting({
    //             projectId: project,
    //             reportDocumentId,
    //             file: fileObj,
    //         });
    //         const preview = docxImportStore.previewData;
    //         setDocxPreviewData(preview);
    //         setDocName(reportDocument?.name || "");
    //         setAcceptedSections(
    //             Object.fromEntries(
    //                 (preview.sections || []).map((section, idx) => [
    //                     idx,
    //                     { status: section.status, accepted: true },
    //                 ]),
    //             ),
    //         );
    //         setImportModalVisible(true);
    //     } catch (err) {
    //         // error is handled by the store
    //     }
    // };

    const handleImportModalSave = async () => {
        const sectionsToSave = docxPreviewData.sections
            .filter(
                (section, idx) =>
                    acceptedSections[idx]?.accepted &&
                    (section.status === "new" || section.status === "modified"),
            )
            .map((section) => {
                if (section.status === "modified" && section.oldSection?._id) {
                    return { ...section, _id: section.oldSection._id };
                }
                return section;
            });
        const commentsToSave = (docxPreviewData.comments || []).filter(
            (comment) => acceptedSections[comment.section],
        );
        await docxImportStore.saveImportToExisting({
            projectId: project,
            reportDocumentId,
            docName,
            sections: sectionsToSave,
            comments: commentsToSave,
        });
        setImportModalVisible(false);
        const updatedSections = await reportDocumentsStore.getReportDocumentSections(
            reportDocumentId,
        );
        await load(updatedSections);
    };

    const handleImportModalCancel = () => {
        setImportModalVisible(false);
        setDocxPreviewData({ sections: [], comments: [] });
        setDocName("");
        setAcceptedSections({});
    };

    const updateAcceptedSections = (idx, status, accepted) => {
        setAcceptedSections((prev) => ({
            ...prev,
            [idx]: { status, accepted },
        }));
    };

    return (
        <Page className={"report-document"}>
            <Page.Header closable={true} onClose={close} className={"report-document-header"}>
                <Page.Header.Left shrink>
                    <Page.Title>
                        <div>{reportDocument?.name}</div>
                    </Page.Title>
                </Page.Header.Left>
                <Page.Header.Right>
                    {reportDocument.status === ReportDocumentStatus.IN_PROGRESS && (
                        <>
                            {reviewStage?.title && (
                                <label>
                                    Current review stage:
                                    <b> {reviewStage.title}</b>
                                </label>
                            )}

                            {reviewStage?.reviewers?.length > 0 && (
                                <>
                                    <div className={"vl"} />
                                    <Status.AssignedUsers
                                        users={reportDocument.metaData?.currentStage?.reviewers}
                                    />
                                </>
                            )}
                        </>
                    )}

                    {reportDocument.status === ReportDocumentStatus.IN_PROGRESS &&
                        userReviewer &&
                        (userReviewer.reviewed ? (
                            <Button type={"primary"} icon={<CheckOutlined />}>
                                Review completed
                            </Button>
                        ) : (
                            !readonly && (
                                <Button
                                    type={"primary"}
                                    icon={<EyeOutlined />}
                                    onClick={markReviewed}
                                >
                                    Mark as reviewed
                                </Button>
                            )
                        ))}

                    {reportDocument.status === ReportDocumentStatus.REVIEWED && (
                        <label className="allReivewsCompleted">All reviews are completed</label>
                    )}

                    {reviewStarted && !hasReviewers && (
                        <label className="noReviewers">Please setup reviewers</label>
                    )}

                    {/*<UploadContextProvider projectId={project} clientId={null}>
                        <UploadButton
                            icon={<UploadOutlined />}
                            label="Import DOCX"
                            accept=".docx"
                            onChange={handleDocxImport}
                            primary={false}
                        />
                    </UploadContextProvider>*/}

                    <Dropdown overlay={exportMenu} trigger="click" placement="bottomRight">
                        <Button>
                            Export <DownOutlined />
                        </Button>
                    </Dropdown>

                    {antdHelpers.hasMenuItems(menuItems) && (
                        <Dropdown overlay={menuItems} trigger="click" placement="bottomRight">
                            <Button
                                type="default"
                                icon={<EllipsisOutlined />}
                                style={{ border: "none" }}
                            />
                        </Dropdown>
                    )}
                </Page.Header.Right>
            </Page.Header>
            <Page.Layout>
                <Page.Body padding={false}>
                    {loading && (
                        <div className="loader">
                            <Loader loading={true} />
                        </div>
                    )}
                    {!loading && (
                        <Viewer
                            sections={sectionsStore.sections}
                            dictionary={dictionaryStore}
                            citations={citationsStore}
                            abbreviations={abbreviationStore}
                            editingState={[editing, setEditing]}
                            onReload={load}
                            readonly={readonly}
                            documentId={reportDocument._id}
                        />
                    )}
                </Page.Body>
                <ReportDocumentSidebar width={350} readonly={readonly} />
            </Page.Layout>
            <ReviewOptionsModal
                visible={reviewModalVisible}
                onCancel={handleReviewCancel}
                onConfirm={handleReviewConfirm}
            />
            <DocxImportDiffModal
                visible={importModalVisible}
                onCancel={handleImportModalCancel}
                onSave={handleImportModalSave}
                previewData={docxPreviewData}
                docName={docName}
                setDocName={setDocName}
                acceptedSections={acceptedSections}
                setAcceptedSections={updateAcceptedSections}
            />
        </Page>
    );
});

ReportDocument.displayName = "ReportDocument";

export default ReportDocument;
